import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Route, Switch, useParams, useHistory } from 'react-router-dom';
import { CssBaseline } from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import App from './App';
import AppStateProvider, { useAppState } from './state';
import ErrorDialog from './components/ErrorDialog/ErrorDialog';
import theme from './theme';
import Nothing from './components/Nothing/Nothing';
import { IntlProvider } from 'react-intl';
import localeService from './utils/locale-service';
import { setupTourlaneCookies, SnowplowScript, setup, snowplow, addToGlobalContext } from '@tourlane/tracking';

import { initSentry, setSentryUser } from './utils/sentry';

initSentry();

const initSnowplow = () => {
  setupTourlaneCookies();
  const customerUuid = new URLSearchParams(window.location.search).get('customer_uuid');
  if (customerUuid) {
    addToGlobalContext([
      {
        data: {
          customer_uuid: customerUuid,
        },
        schema: snowplow.Schema.TOURLANE_CUSTOMER_ENTITY,
      },
    ]);
  }
  setup(null);
};

export const VideoApp = () => {
  const { error, setError, setUser, getTravelRequest, locale, setLocale, setCall } = useAppState();
  const { travelRequestUuid } = useParams<{ travelRequestUuid: string }>();
  const history = useHistory();
  const [messagesInitialized, setmessagesInitialized] = useState(false);
  const [messages, setMessages] = useState({});

  useEffect(() => {
    const localeFromUrl = new URLSearchParams(window.location.search).get('lang');
    setLocale(localeFromUrl || window.navigator.language);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchTravelRequest = async (id?: string) => {
      if (id) {
        const data = await getTravelRequest(id);
        // CM resolves other ids a link may carry (e.g. an appointment uid) to the lead. Zoom's
        // lead_uuid and the Sentry user come from the route param, so it has to be the lead UUID.
        if (data.leadUuid && data.leadUuid !== id) {
          history.replace({ pathname: `/t/${data.leadUuid}`, search: history.location.search });
        }
        setUser({ displayName: data.customerFullName });
        setCall({
          destination: data.destination,
          agentName: data.agentName,
          dateAndTime: data.dateAndTime,
          schedulingMode: data.schedulingMode,
        });
      }
    };
    // getTravelRequest already captures and sets `error` before rethrowing.
    fetchTravelRequest(travelRequestUuid).catch(() => {});
    setSentryUser(travelRequestUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelRequestUuid]);

  useEffect(() => {
    const translations = localeService.getMessages(locale);
    if (translations) {
      setMessages(translations);
      document.title = translations['page_title'] || document.title;
    }
    setmessagesInitialized(true);
  }, [locale]);

  return (
    <>
      {/* error is only ever set by a getTravelRequest failure — redirect home once it's dismissed. */}
      <ErrorDialog
        dismissError={() => {
          setError(null);
          history.replace('/');
        }}
        error={error}
      />
      {messagesInitialized && (
        <IntlProvider messages={messages} key={locale} locale={locale} defaultLocale="en">
          <App />
        </IntlProvider>
      )}
    </>
  );
};

export const ReactApp = () => (
  <>
    {process.env.REACT_APP_DEPLOYED_TO && (
      <SnowplowScript environment={process.env.REACT_APP_DEPLOYED_TO} appId="video-chat" callback={initSnowplow} />
    )}
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppStateProvider>
          <Switch>
            <Route exact path="/t/:travelRequestUuid" component={VideoApp} />
            <Route path="*">
              <Nothing />
            </Route>
          </Switch>
        </AppStateProvider>
      </Router>
    </MuiThemeProvider>
  </>
);

const rootElement = document.getElementById('root');
if (rootElement) {
  ReactDOM.render(<ReactApp />, rootElement);
} else if (process.env.NODE_ENV !== 'test') {
  // A missing #root is a fatal misconfiguration outside of tests (where
  // jsdom has no such element) — fail loudly instead of silently doing nothing.
  throw new Error('Failed to find the root element to mount the app into. Expected a <div id="root"> in the page.');
}
